import { useQuery } from '@tanstack/react-query';

import { getPatientTreatments } from '../../../api/patients';
import { isMeshProcessing, isMeshStalled, type MeshAsset, type Treatment } from '../../../types/admin';
import { useAuth } from '../../auth/AuthProvider';

type UseTreatmentsOptions = {
  readonly enabled?: boolean;
};

/** How often to re-poll while the server is still converting an uploaded STL to GLB. */
const MESH_PROCESSING_POLL_MS = 3000;

function everyCycleMesh(treatments: readonly Treatment[] | undefined): readonly MeshAsset[] {
  if (!treatments) return [];
  return treatments.flatMap((treatment) =>
    (treatment.cycles ?? []).flatMap((cycle) => [
      ...(cycle.plan_options ?? []).flatMap((option) => [...(option.meshes ?? [])]),
      ...(cycle.movement_steps ?? []).flatMap((step) => [...(step.meshes ?? [])]),
    ]),
  );
}

/**
 * True while any plan or movement-step mesh is still `pending`/`processing`.
 *
 * Mesh conversion runs in a background process pool with no push channel of its
 * own, so the modeling tab polls until every upload settles on `ready`/`failed`.
 */
export function hasProcessingMeshes(treatments: readonly Treatment[] | undefined): boolean {
  return everyCycleMesh(treatments).some(isMeshProcessing);
}

/**
 * Polling continues only while at least one mesh is BOTH unsettled and still
 * within its expected conversion window.
 *
 * Without the second condition a mesh whose worker died would keep the CRM
 * polling every 3 s for the rest of the session.
 */
export function hasActiveProcessing(
  treatments: readonly Treatment[] | undefined,
  now: number,
): boolean {
  return everyCycleMesh(treatments).some(
    (mesh) => isMeshProcessing(mesh) && !isMeshStalled(mesh, now),
  );
}

export function useTreatments(patientId: string, options: UseTreatmentsOptions = {}) {
  const { tokens } = useAuth();

  return useQuery({
    queryKey: ['treatments', patientId],
    queryFn: () => getPatientTreatments(patientId, tokens!.access_token),
    enabled: Boolean(patientId && tokens?.access_token && (options.enabled ?? true)),
    refetchInterval: (query) =>
      hasActiveProcessing(query.state.data, Date.now()) ? MESH_PROCESSING_POLL_MS : false,
  });
}
